<template>
  <page-loader>
    <filter-sidebar
      v-model="filterDrawerOpen"
      :loading="isLoading"
      :show-clear="isBeingFiltered"
      @clear="onClearFilters"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <select-list
              v-model="query.params.country"
              :disabled="!canPerformActions"
              :items="getItemsForOption('countries')"
              label="Country"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.language"
              :disabled="!canPerformActions"
              :items="languageOptions"
              label="Language"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.site"
              :disabled="!canPerformActions"
              :items="getItemsForOption('sites')"
              label="Website"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <date-range-picker v-model="query.params.dateRange"></date-range-picker>
          </v-col>
        </v-row>
      </v-container>
    </filter-sidebar>

    <v-container>
      <v-row>
        <v-col>
          <filter-chips :data="filterChipsData" @clear-chip="$_filterChips_clearChip">
            <v-badge color="accent" bordered overlap :content="$_queryHandler_numberOfQueries">
              <v-btn color="accent" depressed small title="Filters" @click="filterDrawerOpen = true">
                <v-icon left small>filter_alt</v-icon>
                <span>Filters</span>
              </v-btn>
            </v-badge>
          </filter-chips>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              <v-col class="d-flex pa-0">
                <span>{{ pagination.total }} email addresses</span>
                <v-spacer></v-spacer>
                <v-btn :disabled="isLoading" :loading="isLoading" color="primary" depressed @click="onClickExport">
                  Export as CSV
                </v-btn>
              </v-col>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
// Helpers
import { format } from 'date-fns'
import { mapGetters, mapMutations } from 'vuex'
import { exportCSV } from '@/utils/csv'
import isSet from '@/utils/is-set'
// Components
import FilterChips from '@/components/filter-chips'
import FilterSidebar from '@/components/filter-sidebar'
import DateRangePicker from '@/components/date-range-picker'
import PageLoader from '@/components/page-loader'
import SelectList from '@/components/forms/select-list'
// Mixins
import FilterChipsHandler from '@/mixins/filter-chips'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { emails, languages } from '@/services/offers'

const formatDate = (date = new Date()) => format(date, 'yyyy-MM-dd')

export default {
  name: 'RevenueReport',
  components: {
    FilterChips,
    FilterSidebar,
    DateRangePicker,
    PageLoader,
    SelectList,
  },
  mixins: [FilterChipsHandler, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    isLoading: false,
    emails: [],
    filterDrawerOpen: false,
    defaultQuery: {
      country: undefined,
      language: undefined,
      site: undefined,
      dateRange: [formatDate(), formatDate()],
    },
    languageOptions: [],
    queriesDefined: ['country', 'language', 'site', 'dateRange'],
    queryDefaults: ['country', 'language', 'site', 'dateRange'],
  }),
  computed: {
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    filterChipsData() {
      return [
        {
          label: 'Country',
          model: 'query.params.country',
          options: this.getItemsForOption('countries'),
          value: this.query.params.country,
        },
        {
          label: 'Language',
          model: 'query.params.language',
          options: this.languageOptions,
          value: this.query.params.language,
        },
        {
          label: 'Site',
          model: 'query.params.site',
          options: this.getItemsForOption('sites'),
          value: this.query.params.site,
        },
        {
          label: 'Date range',
          model: 'query.params.dateRange',
          format: 'daterange',
          value: this.query.params.dateRange,
        },
      ]
    },
    isBeingFiltered() {
      return (
        Object.entries(this.query.params).some(([param, value]) => {
          switch (param) {
            case 'dateRange':
              return String(value) !== String([formatDate(), formatDate()])
            default:
              return isSet(value)
          }
        }) || this.query.searchTerm
      )
    },
    queryFilters() {
      const {
        params: { country, dateRange, language, site },
      } = this.query

      const preppedFilters = [
        country && ['countryCode', country],
        language && ['language', language],
        site && ['site', site],
        dateRange[0] && ['dateGreaterThanOrEqualTo', dateRange[0]],
        dateRange[1] && ['dateLessThanOrEqualTo', dateRange[1]],
      ]
        .filter(Boolean)
        .reduce((filters, [key, value]) => ({ ...filters, [key]: this.handleParamValue(value) }), {})

      return Object.keys(preppedFilters).length ? preppedFilters : undefined
    },
  },
  async created() {
    await this.fetchLanguageOptions()
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async fetchLanguageOptions() {
      this.isLoading = true
      try {
        this.languageOptions = (await languages.getLanguages()).map(({ name, code }) => ({ key: name, value: code }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch translation language options',
          duration: -1,
          data: { error },
        })
      }
      this.isLoading = false
    },
    async getEmails() {
      this.isLoading = true
      const { perPage, currentPage: page } = this.pagination

      try {
        const { data, pagination } = await emails.getEmails({
          page,
          perPage,
          filters: this.queryFilters,
        })
        this.emails = data || []
        this.pagination = pagination
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve emails',
        })
      }

      this.setPageLoading(false)
      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getEmails()
    },
    async onClickExport() {
      try {
        const data = await emails.getEmails({
          filters: this.queryFilters,
          format: 'csv',
        })
        exportCSV(data, 'emails')
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to export emails',
        })
      }
    },
    onClearFilters() {
      Object.keys(this.query.params).forEach(param => {
        switch (param) {
          case 'dateRange':
            this.$set(this.query.params, 'dateRange', [formatDate(), formatDate()])
            break
          default:
            this.$set(this.query.params, param, null)
        }
      })
    },
  },
}
</script>
